/*
 * /assets/store-locator.js
 * Client-side filtering for the Find a Store section. Reads:
 *   - <script type="application/json" data-store-data>   full store list
 *   - <input data-store-search>                          search input
 *   - <div  data-state-filter>                           container for country chips
 *   - <article data-store data-state data-search>...     card elements
 *   - <section data-state-group>                          group wrappers
 *   - <p data-result-count data-count-template-one/-other>  count line; each
 *     template is a translated string holding a literal {{ count }} token
 *   - <div  data-empty>                                  empty-state container
 *
 * URL params:
 *   ?q=<term>      Pre-fills the search box and applies the filter on load.
 *   ?state=<code>  Activates a specific country chip on load (e.g. ?state=DK).
 *
 * Mobile behaviour (<= 699px): the chip row scrolls horizontally, so tapping a
 * chip centres it in that row and then scrolls the page to the result count,
 * otherwise the filtered results start below the fold and the filter looks
 * like it did nothing.
 */

(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 699px)';

  function init(section) {
    if (!section || section.dataset.storeLocatorReady === '1') return;
    section.dataset.storeLocatorReady = '1';

    var dataEl = section.querySelector('[data-store-data]');
    var searchInput = section.querySelector('[data-store-search]');
    var chipContainer = section.querySelector('[data-state-filter]');
    var resultsContainer = section.querySelector('[data-results]');
    var countEl = section.querySelector('[data-count]');
    var countLine = section.querySelector('[data-result-count]');
    var emptyEl = section.querySelector('[data-empty]');
    var tplOne = countLine ? countLine.getAttribute('data-count-template-one') : '';
    var tplOther = countLine ? countLine.getAttribute('data-count-template-other') : '';

    if (!dataEl || !searchInput || !chipContainer || !resultsContainer) return;

    var stores = [];
    try {
      stores = JSON.parse(dataEl.textContent || '[]');
    } catch (err) {
      console.warn('[store-locator] Failed to parse store data:', err);
      stores = [];
    }

    var cards = Array.prototype.slice.call(resultsContainer.querySelectorAll('[data-store]'));
    var groups = Array.prototype.slice.call(resultsContainer.querySelectorAll('[data-state-group]'));

    var stateMap = {};
    stores.forEach(function (s) {
      if (s.state && !stateMap[s.state]) {
        stateMap[s.state] = s.state_name || s.state;
      }
    });

    var stateCodes = Object.keys(stateMap).sort(function (a, b) {
      return stateMap[a].localeCompare(stateMap[b]);
    });

    stateCodes.forEach(function (code) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'store-locator__chip';
      btn.setAttribute('data-state-chip', code);
      btn.textContent = stateMap[code];
      chipContainer.appendChild(btn);
    });

    var activeState = 'all';
    var activeQuery = '';

    function normalize(s) {
      return (s || '').toLowerCase().trim();
    }

    function isMobile() {
      return window.matchMedia && window.matchMedia(MOBILE_QUERY).matches;
    }

    function centerChip(chip) {
      if (!chip || typeof chip.scrollIntoView !== 'function') return;
      try {
        chip.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
      } catch (err) {
        chip.scrollIntoView(false);
      }
    }

    function scrollToResults() {
      if (!isMobile()) return;
      var anchor = countLine || resultsContainer;
      if (!anchor) return;

      var headerHeight = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
        10
      ) || 0;

      var top = anchor.getBoundingClientRect().top + (window.pageYOffset || 0) - headerHeight - 16;

      try {
        window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
      } catch (err) {
        window.scrollTo(0, Math.max(top, 0));
      }
    }

    function applyFilter() {
      var query = normalize(activeQuery);
      var visibleCount = 0;
      var groupVisibility = {};

      cards.forEach(function (card) {
        var cardState = card.getAttribute('data-state') || '';
        var haystack = card.getAttribute('data-search') || '';

        var matchesState = activeState === 'all' || cardState === activeState;
        var matchesQuery = query === '' || haystack.indexOf(query) !== -1;
        var visible = matchesState && matchesQuery;

        if (visible) {
          card.classList.remove('is-hidden');
          visibleCount++;
          groupVisibility[cardState] = true;
        } else {
          card.classList.add('is-hidden');
        }
      });

      groups.forEach(function (group) {
        var code = group.getAttribute('data-state-group') || '';
        if (groupVisibility[code]) {
          group.classList.remove('is-hidden');
        } else {
          group.classList.add('is-hidden');
        }
      });

      if (countLine && (tplOne || tplOther)) {
        var tpl = visibleCount === 1 ? (tplOne || tplOther) : (tplOther || tplOne);
        countLine.textContent = tpl.replace('{{ count }}', String(visibleCount)).replace('{{count}}', String(visibleCount));
      } else if (countEl) {
        countEl.textContent = String(visibleCount);
        var label = countEl.parentNode;
        if (label && label.lastChild && label.lastChild.nodeType === 3) {
          label.lastChild.nodeValue = ' ' + (visibleCount === 1 ? 'store' : 'stores');
        }
      }

      if (emptyEl) {
        if (visibleCount === 0 && stores.length > 0) {
          emptyEl.hidden = false;
          resultsContainer.setAttribute('hidden', '');
        } else {
          emptyEl.hidden = true;
          resultsContainer.removeAttribute('hidden');
        }
      }
    }

    var searchDebounce;
    searchInput.addEventListener('input', function () {
      activeQuery = searchInput.value;
      clearTimeout(searchDebounce);
      searchDebounce = setTimeout(applyFilter, 80);
    });

    chipContainer.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.matches || !target.matches('[data-state-chip]')) return;
      var code = target.getAttribute('data-state-chip');
      if (!code) return;

      activeState = code;
      Array.prototype.forEach.call(chipContainer.querySelectorAll('[data-state-chip]'), function (chip) {
        chip.classList.toggle('is-active', chip === target);
      });
      applyFilter();
      centerChip(target);
      scrollToResults();
    });

    function readUrlParams() {
      try {
        var params = new URLSearchParams(window.location.search);
        var q = params.get('q');
        var stateParam = params.get('state');

        if (q) {
          searchInput.value = q;
          activeQuery = q;
        }

        if (stateParam) {
          var code = stateParam.toUpperCase();
          if (stateMap[code]) {
            activeState = code;
            var chip = chipContainer.querySelector('[data-state-chip="' + code + '"]');
            if (chip) {
              Array.prototype.forEach.call(chipContainer.querySelectorAll('[data-state-chip]'), function (c) {
                c.classList.toggle('is-active', c === chip);
              });
              centerChip(chip);
            }
          }
        }
      } catch (err) {}
    }

    readUrlParams();
    applyFilter();
  }

  function boot() {
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-section-type="store-locator"]'),
      init
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector) {
      var section = event.target.querySelector('[data-section-type="store-locator"]');
      if (section) init(section);
    }
  });
})();